import { useRef, useContext, useState, useEffect } from 'react';

import styles from './ManageParticipantsComponent.module.css'

import { GlobalContext } from "../../../contexts/globalContext";
import { ProjectDetailsContext } from '../../../contexts/projectDetailsContext';
import { AddConferenceContext } from '../../../contexts/addConferenceContext';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-fe6b9b7683/icons';

import { GetMaxZIndex } from '../../../utils/UtilitaryFunctions';

import NotAttachedParticipantsComponent from './NotAttachedParticipantsComponent';
import DisplayRoomComponent from './DisplayRoomComponent';
import ConferenceAddVisitorComponent from './ConferenceAddVisitorComponent';
import AddGuestComponent from '../../popup/addParticipantsParts/AddGuestsComponent';
import LightButtonComponent from '../../commons/LightButtonComponent';


const ManageParticipantsComponent = ({ isConference = false }) => {
    
    const { socketRef, selectedProject, currentConference, addGuest } = useContext(GlobalContext);
    const { setEditConference, setCreateConference, setCreateMeeting } = useContext(ProjectDetailsContext)
    const { conferenceDetailsForParticipants, setConferenceDetailsForParticipants, setOpenParticipants, addVisitor, setUniqueModerators } = useContext(AddConferenceContext);

    const allParticipantsRef = useRef()
    const currentConferenceRef = useRef();
    const ref = useRef();

    const [displayedParticipants, setDisplayedParticipants] = useState([])
    const [attachedParticipants, setAttachedParticipants] = useState([])
    const [allDisplayedDesks, setAllDisplayedDesks] = useState([])
    const [participantsFilter, setParticipantsFilter] = useState()
    const [displayedRoom, setDisplayedRoom] = useState()
    const [deskHeight, setDeskHeight] = useState(70)
    const [allRooms, setAllRooms] = useState([])
    const [isRemote, setIsRemote] = useState(false)
    const [tablePosition, setTablePosition] = useState()
    const [selectedInvites, setSelectedInvites] = useState([]);
    const [verifyRoomAdmin, setVerifyRoomAdmin] = useState(true)
    const [currentRoomAdmin, setCurrentRoomAdmin] = useState()
    const [verifyFilters, setVerifyFilters] = useState(false)

    useEffect(() => {
        if (conferenceDetailsForParticipants) {
            currentConferenceRef.current = conferenceDetailsForParticipants;
        } else {
            currentConferenceRef.current = currentConference;
        }

        socketRef.current.on('REFRESH_PARTICIPANTS_TO_ADD', (projectId) => {
            if (selectedProject?._id === projectId || currentConference?.project === projectId) {
                socketRef.current.emit("GET_CONFERENCE_PARTICIPANTS_TO_ADD",
                    selectedProject ? selectedProject._id : currentConference.project,
                    conferenceDetailsForParticipants ? conferenceDetailsForParticipants._id : currentConference._id,
                    isRemote,
                    (callback) => {
                        if (!callback.error) {
                            allParticipantsRef.current = callback.response
                            setDisplayedParticipants(callback.response)
                        }
                    }
                )
            }
        })

        if (currentConferenceRef.current.type !== "Meeting") {
            socketRef.current.emit('GET_CONFERENCE_ROOMS_BY_ID', (currentConferenceRef.current.type === "Presentation") ? null : currentConferenceRef.current.rooms, callback => {
                setAllRooms(callback);
                if (callback && callback.length > 0) {
                    setDisplayedRoom(callback[0]);
                } else {
                    setIsRemote(true);
                }
            })
        } else {
            setIsRemote(true);
        }


        ref.current.style.zIndex = GetMaxZIndex() + 1;

        return () => {
            if (conferenceDetailsForParticipants) {
                socketRef.current.emit("GET_CONFERENCE_BY_ID", currentConferenceRef.current._id, (callback) => {
                    setEditConference?.(callback.response)
                    setConferenceDetailsForParticipants?.(false)
                })
            }
            socketRef.current?.off('REFRESH_PARTICIPANTS_TO_ADD')
        }

    }, [])

    useEffect(() => {
        if (currentConference?.type === "Conference" && currentConference.moderators) {
            setUniqueModerators(currentConference.moderators.filter((moderator, index, self) =>
                index === self.findIndex((mod) => mod._id === moderator?._id)
            ))
        }
    },[currentConference])

    const changeRooms = (room) => {
        setDisplayedRoom(room);
        setIsRemote(false);
    }

    useEffect(() => {
        if (displayedRoom && !isRemote) {
            socketRef.current.emit("CONFERENCE_GET_ATTACHED_PARTICIPANTS_FROM_ROOM", {
                conferenceId: currentConferenceRef.current._id,
                roomId: displayedRoom._id
            }, callback => {
                if (!callback.error) {
                    setAttachedParticipants(callback.response)
                    setAllDisplayedDesks(getAllDesks(displayedRoom))
                }
            })

            var newDskHeight = 70;

            var mxx = Math.max(displayedRoom.desks_east?.length, displayedRoom.desks_west?.length);

            if (mxx > 0) {
                var discac = Math.trunc(1200 / (mxx + 1.5));

                if (discac > 0) {
                    newDskHeight = discac;
                }
            }

            setDeskHeight(newDskHeight);
        }
    }, [displayedRoom])

    useEffect(() => {
        if (isRemote) {
            socketRef.current.emit("CONFERENCE_GET_REMOTE_PARTICIPANTS",
                currentConferenceRef.current._id,
                callback => {
                    if (!callback.error) {
                        setAttachedParticipants(callback.response)
                    }
                })
        }

        socketRef.current.emit("GET_CONFERENCE_PARTICIPANTS_TO_ADD",
            selectedProject ? selectedProject?._id : currentConference?.project,
            conferenceDetailsForParticipants ? conferenceDetailsForParticipants?._id : currentConference?._id,
            isRemote,
            displayedRoom ? displayedRoom?.location._id : null,
            (callback) => {
                if (!callback.error) {
                    allParticipantsRef.current = callback.response
                    setDisplayedParticipants(callback.response)
                    setVerifyFilters(true)
                }
            }
        )

    }, [isRemote, displayedRoom])

    const getAllDesks = (room) => {
        const { desks_east, desks_north, desks_south, desks_west } = room;

        const allDesks = [
            ...desks_east.map((desk) => ({
                _id: desk._id,
                number: parseInt(desk.number, 10),
                partOf: 'desks_east',
            })),
            ...desks_north.map((desk) => ({
                _id: desk._id,
                number: parseInt(desk.number, 10),
                partOf: 'desks_north',
            })),
            ...desks_south.map((desk) => ({
                _id: desk._id,
                number: parseInt(desk.number, 10),
                partOf: 'desks_south',
            })),
            ...desks_west.map((desk) => ({
                _id: desk._id,
                number: parseInt(desk.number, 10),
                partOf: 'desks_west',
            })),
        ];

        allDesks.sort((a, b) => a.number - b.number);
        return allDesks;
    };

    const closePage = () => {
        setConferenceDetailsForParticipants?.();
        setCreateConference?.();
        setOpenParticipants(false);
        setCreateMeeting?.()
    }


    return (
        <div ref={ref} className={styles.component} style={{ width: isConference ? 'calc(100% - 135px)' : '100%', marginLeft: isConference ? '135px' : 0 }}>
            <div style={{
                position: "absolute",
                bottom: 30,
                right: 37,
                display: 'flex',
                flexDirection: 'row',
            }}>

                {!isConference &&
                    <LightButtonComponent style={{ width: 353, borderRadius: 8 }} onClick={closePage}>
                        <FontAwesomeIcon className={styles.iconBack} icon={byPrefixAndName.fas['arrow-left']} />Back
                    </LightButtonComponent>
                }

            </div>
            <NotAttachedParticipantsComponent setParticipantsFilter={setParticipantsFilter} participantsFilter={participantsFilter} displayedRoom={displayedRoom} isRemote={isRemote} 
                allDisplayedDesks={allDisplayedDesks} displayedParticipants={displayedParticipants} verifyRoomAdmin={verifyRoomAdmin} setVerifyRoomAdmin={setVerifyRoomAdmin}
                setDisplayedParticipants={setDisplayedParticipants} attachedParticipants={attachedParticipants} setAttachedParticipants={setAttachedParticipants}
                allParticipantsRef={allParticipantsRef} tablePosition={tablePosition} deskHeight={deskHeight} selectedInvites={selectedInvites} setSelectedInvites={setSelectedInvites} 
                setCurrentRoomAdmin = {setCurrentRoomAdmin} currentRoomAdmin={currentRoomAdmin} verifyFilters={verifyFilters} setVerifyFilters={setVerifyFilters}/>

            <div className={styles.rightBody}>

                <DisplayRoomComponent setDisplayedRoom={setDisplayedRoom} displayedRoom={displayedRoom} deskHeight={deskHeight} allRooms={allRooms}
                    isRemote={isRemote} setIsRemote={setIsRemote} changeRooms={changeRooms} attachedParticipants={attachedParticipants} setTablePosition={setTablePosition}
                    setDisplayedParticipants={setDisplayedParticipants} allParticipantsRef={allParticipantsRef} setAttachedParticipants={setAttachedParticipants}
                    participantsFilter={participantsFilter} setParticipantsFilter={setParticipantsFilter} selectedInvites={selectedInvites} setSelectedInvites={setSelectedInvites} 
                    verifyRoomAdmin={verifyRoomAdmin} setVerifyRoomAdmin={setVerifyRoomAdmin} currentRoomAdmin={currentRoomAdmin} setCurrentRoomAdmin={setCurrentRoomAdmin}/>

            </div>


            {addVisitor && <ConferenceAddVisitorComponent setDisplayedParticipants={setDisplayedParticipants} allParticipantsRef={allParticipantsRef}
                participantsFilter={participantsFilter} setParticipantsFilter={setParticipantsFilter} displayedRoom={displayedRoom}/>}

            {addGuest && <AddGuestComponent setDisplayedParticipants={setDisplayedParticipants} allParticipantsRef={allParticipantsRef}
                participantsFilter={participantsFilter} setParticipantsFilter={setParticipantsFilter} refreshParticipantsList={true}/>}

        </div>
    );

}

export default ManageParticipantsComponent;